import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTitleAndUser } from '../../../../../hooks/userTitle';
import { ArrowLeft, ArrowUp, Buildings, Info } from '@phosphor-icons/react';
import { SideModal } from '../../../../../components/SideModal';
import {
  ContentList,
  RowDiv,
  WarningNewMessage,
} from '../../acoesPainelStyles';
import AsideFiltersReits from './AsideFiltersReits';
import api from '../../../../../services/api';
import { financeRoutes } from '../../../../../utils/RoutesApi';
import { CardStockRank } from '../Internacionais/GraphicStocks.jsx';

const PainelContainer = styled.div`
  width: 100%;
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 10px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const ContentPlaceholder = styled.div`
  padding: 1rem 0;
  color: #455a64;
  margin-top: 4rem;
`;

export default function ReitsPainel({
  tipo = 'reit',
  isComponent = false,
  ticketActive = '',
  selectedActive = ({ slug, type }) => {},
}) {
  const { setTitle } = useTitleAndUser();
  const [modalFilter, setModalFilter] = useState(false);

  const [searchFilters, setSearchFilters] = useState({
    nome_ativo: '',
    tipo_ativo: tipo,
    currency: '',
    accounting_standard: '',
    bolsa: '',
    isFiltered: false,
  });

  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageUrl, setNextPageUrl] = useState(null);

  const [reits, setReits] = useState([]);
  const [loadingReits, setLoadingReits] = useState(false);

  async function loadMoreReits() {
    if (loadingMore) return;
    if (!nextPageUrl) return;

    try {
      setLoadingMore(true);

      const nextPage =
        typeof nextPageUrl === 'number' ? nextPageUrl : currentPage + 1;

      const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
        params: {
          asset_type: 'REIT',
          is_active: true,
          search: searchFilters.nome_ativo || undefined,
          exchange: searchFilters.bolsa || undefined,
          page: nextPage,
          page_size: 21,
        },
      });

      const nextReits = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.results || [];

      const totalLoaded = reits.length + nextReits.length;
      const totalCount = response.data?.count || totalLoaded;
      const current = response.data?.current || nextPage;

      setCurrentPage(current);
      setNextPageUrl(totalLoaded < totalCount ? current + 1 : null);

      const nextReitsWithDetails = await enrichReitsWithDetails(nextReits);

      setReits((prev) => [...prev, ...nextReitsWithDetails]);
    } finally {
      setLoadingMore(false);
    }
  }

  function handleChangeSearch(event) {
    const value = event.target.value;

    setSearchFilters((prev) => ({
      ...prev,
      nome_ativo: value,
      isFiltered: value.length > 0,
    }));
  }

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!isComponent) {
      setTitle('REITs');
    }
  }, [isComponent, setTitle]);

  useEffect(() => {
    function handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.offsetHeight;

      if (scrollPosition >= pageHeight - 300) {
        loadMoreReits();
      }
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [reits, loadingMore, nextPageUrl, searchFilters.nome_ativo, searchFilters.bolsa]);

  useEffect(() => {
    setSearchFilters((prev) => ({
      ...prev,
      tipo_ativo: tipo,
    }));
  }, [tipo]);

  useEffect(() => {
    async function loadReits() {
      try {
        const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
          params: {
            asset_type: 'REIT',
            is_active: true,
            search: searchFilters.nome_ativo || undefined,
            exchange: searchFilters.bolsa || undefined,
            page: 1,
            page_size: 21,
          },
        });

        const reitsList = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.results || [];

        const totalCount = response.data?.count || reitsList.length;
        const current = response.data?.current || 1;

        setCurrentPage(current);
        setNextPageUrl(reitsList.length < totalCount ? current + 1 : null);

        const reitsWithDetails = await enrichReitsWithDetails(reitsList);

        // Ordem definida pelo backend (nota desc); preserva a ordem ao paginar.
        setReits(reitsWithDetails);
      } catch (error) {
        console.error('Erro ao buscar REITs internacionais:', error);
        setReits([]);
      } finally {
        setLoadingReits(false);
      }
    }

    if (tipo === 'reit') {
      loadReits();
    }
  }, [tipo, searchFilters.nome_ativo, searchFilters.bolsa]);

  function calculateReitScore(reit) {
    const kpi = reit?.latest_kpi || reit?.kpi || reit?.kpis || {};

    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return 0;

      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    };

    const clamp = (value, min = 0, max = 100) => {
      return Math.min(Math.max(value, min), max);
    };

    const netMargin = toNumber(kpi.net_margin);
    const ebitdaMargin = toNumber(kpi.ebitda_margin);
    const dividendYield = toNumber(kpi.dy ?? kpi.avg_dy);
    const netDebt = toNumber(kpi.net_debt);
    const netDebtEbitda = toNumber(kpi.net_debt_ebitda);

    const values = [
      clamp(netMargin * 100),
      clamp(ebitdaMargin * 100),
      clamp(dividendYield * 100),
      clamp(netDebt <= 0 ? 100 : 50),
      clamp(netDebtEbitda <= 0 ? 100 : 100 - netDebtEbitda * 20),
    ];

    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  async function enrichReitsWithDetails(reitsList = []) {
    // `latest_kpi` já vem na listagem; o score do ranking é calculado sem
    // buscar o detalhe de cada REIT. O detalhe completo carrega no clique.
    return reitsList.map((reit) => ({
      ...reit,
      // Nota real vinda do backend (asset_score_service); cai na heurística
      // de KPIs só quando o REIT ainda não tem score calculado.
      score: Number(reit.score) || calculateReitScore(reit),
    }));
  }

  const filteredReits = reits.filter((reit) => {
    const search = searchFilters.nome_ativo?.toLowerCase() || '';

    return (
      !search ||
      reit.name?.toLowerCase().includes(search) ||
      reit.ticker?.toLowerCase().includes(search)
    );
  });

  function renderTabTitle() {
    return 'REITs';
  }

  function renderContentPlaceholder() {
    if (loadingReits) {
      return <ContentPlaceholder>Carregando REITs...</ContentPlaceholder>;
    }

    if (!filteredReits.length) {
      return <ContentPlaceholder>Nenhum REIT encontrado.</ContentPlaceholder>;
    }

    return (
      <>
        <RowDiv>
          <CardsGrid>
            {filteredReits.map((reit) => (
              <CardStockRank
                key={reit.id || reit.ticker}
                stock={reit}
                selectedActive={({ slug }) =>
                  selectedActive({
                    slug,
                    type: 'reit',
                  })
                }
              />
            ))}
          </CardsGrid>
        </RowDiv>

        {loadingMore && <ContentPlaceholder>Carregando mais REITs...</ContentPlaceholder>}
      </>
    );
  }

  if (tipo !== 'reit') {
    return null;
  }

  return (
    <div style={{ marginTop: '0%', background: 'transparent' }}>
      <ContentList>
        <div className="backPage" style={{ zIndex: '1' }}>
          <PainelContainer>
            {!isComponent && (
              <>
                <AsideFiltersReits
                  setSearchFilters={setSearchFilters}
                  searchFilters={searchFilters}
                  onChangeSearch={handleChangeSearch}
                  searchPlaceholder={`Buscar em ${renderTabTitle()}`}
                  showMobileButton={window.screen.width < 1100}
                  onOpenMobileFilters={() => setModalFilter(true)}
                />

                <br />

                <WarningNewMessage>
                  <Info color="#EC6643" size={24} />
                  <p>
                    O Ranking tem como objetivo orientar você a iniciar seus
                    estudos pelos ativos mais relevantes. No entanto, ele não
                    deve ser interpretado como uma recomendação específica de
                    compra ou venda.
                  </p>
                </WarningNewMessage>
              </>
            )}

            {renderContentPlaceholder()}
          </PainelContainer>
        </div>
      </ContentList>

      <SideModal
        id="sideFilterInternational"
        isOpen={modalFilter}
        setState={setModalFilter}
      >
        <AsideFiltersReits
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          onChangeSearch={handleChangeSearch}
          searchPlaceholder={`Buscar em ${renderTabTitle()}`}
          showMobileButton={false}
          onOpenMobileFilters={() => setModalFilter(true)}
        />
      </SideModal>
    </div>
  );
}